// Editing a purveyor's profile: its details and types, its pickup locations,
// and its hours. Built for the Admin page (0051, feedback #56) and meant to be
// reused as-is by the Purveyor Portal once members can edit their own
// profile (#57) -- nothing here knows or checks who the editor is; RLS does.
//
// Details are one form with a Save button. Locations and hours are lists
// where each row saves on its own, the same way box items do in the portal,
// so a failed write only ever loses that one row's edit.
import { useState } from "react";
import { DAYS, PURVEYOR_TYPES, FONT_SIZE, SPACE } from "../lib/constants";
import { editorStyles } from "../lib/purveyorEditing";
import {
  addPurveyorLocation,
  updatePurveyorLocation,
  deletePurveyorLocation,
  addPurveyorHours,
  updatePurveyorHours,
  deletePurveyorHours,
} from "../lib/db";

// Name, description, types, main address and contact details. `onSubmit`
// receives the whole profile and may throw; its message is shown.
export function PurveyorProfileForm({ initial, onSubmit, onCancel, submitLabel = "Save", showVerified = false, colors, isMobile }) {
  const s = editorStyles(colors);
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setLoc = (patch) => setDraft((d) => ({ ...d, location: { ...d.location, ...patch } }));
  const setContact = (patch) => setDraft((d) => ({ ...d, contactInfo: { ...d.contactInfo, ...patch } }));
  const toggleType = (t) =>
    setDraft((d) => ({ ...d, purveyorTypes: d.purveyorTypes.includes(t) ? d.purveyorTypes.filter((x) => x !== t) : [...d.purveyorTypes, t] }));

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onSubmit(draft);
    } catch (err) {
      setError(err.message || "Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <label style={s.label}>
        Name
        <input style={s.input} value={draft.name} onChange={(e) => set({ name: e.target.value })} required />
      </label>
      <label style={s.label}>
        Description
        <textarea style={{ ...s.input, minHeight: 72, resize: "vertical" }} value={draft.description} onChange={(e) => set({ description: e.target.value })} />
      </label>
      <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
        <legend style={{ fontSize: FONT_SIZE.sm, color: colors.soft, marginBottom: SPACE.sm }}>Type (one or more)</legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.lg }}>
          {PURVEYOR_TYPES.map((t) => (
            <label key={t} style={{ display: "flex", alignItems: "center", gap: SPACE.xs, fontSize: FONT_SIZE.base, color: colors.ink }}>
              <input type="checkbox" className="mp-check" checked={draft.purveyorTypes.includes(t)} onChange={() => toggleType(t)} />
              {t}
            </label>
          ))}
        </div>
      </fieldset>
      <div style={s.grid(isMobile, "2fr 1fr 0.6fr 0.8fr")}>
        <label style={s.label}>
          Address
          <input style={s.input} value={draft.location.address} onChange={(e) => setLoc({ address: e.target.value })} />
        </label>
        <label style={s.label}>
          City
          <input style={s.input} value={draft.location.city} onChange={(e) => setLoc({ city: e.target.value })} />
        </label>
        <label style={s.label}>
          State
          <input style={s.input} value={draft.location.state} onChange={(e) => setLoc({ state: e.target.value })} />
        </label>
        <label style={s.label}>
          ZIP
          <input style={s.input} value={draft.location.zip} onChange={(e) => setLoc({ zip: e.target.value })} />
        </label>
      </div>
      <div style={s.grid(isMobile, "1fr 1fr")}>
        <label style={s.label}>
          Phone
          <input style={s.input} type="tel" value={draft.contactInfo.phone} onChange={(e) => setContact({ phone: e.target.value })} />
        </label>
        <label style={s.label}>
          Public email
          <input style={s.input} type="email" value={draft.contactInfo.email} onChange={(e) => setContact({ email: e.target.value })} />
        </label>
        <label style={s.label}>
          Website
          <input style={s.input} type="url" placeholder="https://" value={draft.contactInfo.website} onChange={(e) => setContact({ website: e.target.value })} />
        </label>
        <label style={s.label}>
          Social link
          <input style={s.input} type="url" placeholder="https://" value={draft.contactInfo.socialUrl} onChange={(e) => setContact({ socialUrl: e.target.value })} />
        </label>
      </div>
      {showVerified && (
        <label style={{ display: "flex", alignItems: "center", gap: SPACE.sm, fontSize: FONT_SIZE.base, color: colors.ink }}>
          <input type="checkbox" className="mp-check" checked={draft.isVerified} onChange={(e) => set({ isVerified: e.target.checked })} />
          Verified
        </label>
      )}
      <div style={{ display: "flex", gap: SPACE.md }}>
        <button type="submit" className="mp-btn" disabled={busy} style={s.primary}>
          {busy ? "Saving…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="mp-btn" disabled={busy} onClick={onCancel} style={s.secondary}>
            Cancel
          </button>
        )}
      </div>
      {error && <p style={s.error}>{error}</p>}
    </form>
  );
}

const formatPlace = (l) => [l.address, [l.city, l.state].filter(Boolean).join(", "), l.zip].filter(Boolean).join(" · ") || "(no address)";

// Pickup locations. `onChange(updater)` updates the parent's copy of the list.
export function PurveyorLocationsEditor({ purveyorId, locations, onChange, colors, isMobile }) {
  const s = editorStyles(colors);
  const blank = { address: "", city: "", state: "", zip: "" };
  const [editingId, setEditingId] = useState(null); // a location id, "new", or null
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function startEdit(loc) {
    setEditingId(loc ? loc.id : "new");
    setDraft(loc ? { address: loc.address, city: loc.city, state: loc.state, zip: loc.zip } : blank);
    setError("");
  }

  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
      setEditingId(null);
    } catch (err) {
      setError(err.message || "Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      if (editingId === "new") {
        const created = await addPurveyorLocation(purveyorId, draft);
        onChange((list) => [...list, created]);
      } else {
        await updatePurveyorLocation(editingId, draft);
        onChange((list) => list.map((l) => (l.id === editingId ? { ...l, ...draft } : l)));
      }
    });

  const remove = (loc) =>
    run(async () => {
      await deletePurveyorLocation(loc.id);
      onChange((list) => list.filter((l) => l.id !== loc.id));
    });

  const form = (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <div style={s.grid(isMobile, "2fr 1fr 0.6fr 0.8fr")}>
        <input style={s.input} placeholder="Address" value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
        <input style={s.input} placeholder="City" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
        <input style={s.input} placeholder="State" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} />
        <input style={s.input} placeholder="ZIP" value={draft.zip} onChange={(e) => setDraft({ ...draft, zip: e.target.value })} />
      </div>
      <div style={{ display: "flex", gap: SPACE.md }}>
        <button className="mp-btn" disabled={busy} onClick={save} style={s.primary}>Save</button>
        <button className="mp-btn" disabled={busy} onClick={() => setEditingId(null)} style={s.secondary}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      {locations.length === 0 && editingId !== "new" && <p style={{ margin: 0, color: colors.soft, fontSize: FONT_SIZE.base }}>No pickup locations.</p>}
      {locations.map((loc) =>
        editingId === loc.id ? (
          <div key={loc.id}>{form}</div>
        ) : (
          <div key={loc.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, flexWrap: "wrap", fontSize: FONT_SIZE.base }}>
            <span>{formatPlace(loc)}</span>
            <span style={{ display: "flex", gap: SPACE.sm }}>
              <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => startEdit(loc)} style={s.secondary}>Edit</button>
              <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => remove(loc)} style={s.danger}>Delete</button>
            </span>
          </div>
        )
      )}
      {editingId === "new" ? form : (
        <div>
          <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => startEdit(null)} style={s.secondary}>+ Add location</button>
        </div>
      )}
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}

// Opening hours, optionally tied to one pickup location and to a season.
export function PurveyorHoursEditor({ purveyorId, hours, locations, onChange, colors, isMobile }) {
  const s = editorStyles(colors);
  const blank = { dayOfWeek: "Saturday", openTime: "", closeTime: "", locationId: "", seasonStart: "", seasonEnd: "" };
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function startEdit(h) {
    setEditingId(h ? h.id : "new");
    setDraft(h ? { dayOfWeek: h.dayOfWeek, openTime: h.openTime, closeTime: h.closeTime, locationId: h.locationId || "", seasonStart: h.seasonStart, seasonEnd: h.seasonEnd } : blank);
    setError("");
  }

  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
      setEditingId(null);
    } catch (err) {
      setError(err.message || "Couldn't save — try again.");
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      if (editingId === "new") {
        const created = await addPurveyorHours(purveyorId, draft);
        onChange((list) => [...list, created]);
      } else {
        await updatePurveyorHours(editingId, draft);
        onChange((list) => list.map((h) => (h.id === editingId ? { ...h, ...draft, locationId: draft.locationId || null } : h)));
      }
    });

  const remove = (h) =>
    run(async () => {
      await deletePurveyorHours(h.id);
      onChange((list) => list.filter((x) => x.id !== h.id));
    });

  const locationName = (id) => {
    const loc = locations.find((l) => l.id === id);
    return loc ? formatPlace(loc) : "";
  };
  const describe = (h) =>
    [
      `${h.dayOfWeek} ${[h.openTime, h.closeTime].filter(Boolean).join("–")}`.trim(),
      h.locationId ? `at ${locationName(h.locationId)}` : "",
      h.seasonStart || h.seasonEnd ? `season ${h.seasonStart || "…"} to ${h.seasonEnd || "…"}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
  const sorted = [...hours].sort((a, b) => DAYS.indexOf(a.dayOfWeek) - DAYS.indexOf(b.dayOfWeek) || (a.openTime || "").localeCompare(b.openTime || ""));

  const form = (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <div style={s.grid(isMobile, "1fr 1fr 1fr")}>
        <label style={s.label}>
          Day
          <select style={s.input} value={draft.dayOfWeek} onChange={(e) => setDraft({ ...draft, dayOfWeek: e.target.value })}>
            {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label style={s.label}>
          Opens
          <input style={s.input} placeholder="9:00 AM" value={draft.openTime} onChange={(e) => setDraft({ ...draft, openTime: e.target.value })} />
        </label>
        <label style={s.label}>
          Closes
          <input style={s.input} placeholder="1:00 PM" value={draft.closeTime} onChange={(e) => setDraft({ ...draft, closeTime: e.target.value })} />
        </label>
        <label style={s.label}>
          Location
          <select style={s.input} value={draft.locationId} onChange={(e) => setDraft({ ...draft, locationId: e.target.value })}>
            <option value="">Main address</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{formatPlace(l)}</option>)}
          </select>
        </label>
        <label style={s.label}>
          Season starts
          <input style={s.input} type="date" value={draft.seasonStart} onChange={(e) => setDraft({ ...draft, seasonStart: e.target.value })} />
        </label>
        <label style={s.label}>
          Season ends
          <input style={s.input} type="date" value={draft.seasonEnd} onChange={(e) => setDraft({ ...draft, seasonEnd: e.target.value })} />
        </label>
      </div>
      <div style={{ display: "flex", gap: SPACE.md }}>
        <button className="mp-btn" disabled={busy} onClick={save} style={s.primary}>Save</button>
        <button className="mp-btn" disabled={busy} onClick={() => setEditingId(null)} style={s.secondary}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      {hours.length === 0 && editingId !== "new" && <p style={{ margin: 0, color: colors.soft, fontSize: FONT_SIZE.base }}>No hours listed.</p>}
      {sorted.map((h) =>
        editingId === h.id ? (
          <div key={h.id}>{form}</div>
        ) : (
          <div key={h.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, flexWrap: "wrap", fontSize: FONT_SIZE.base }}>
            <span>{describe(h)}</span>
            <span style={{ display: "flex", gap: SPACE.sm }}>
              <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => startEdit(h)} style={s.secondary}>Edit</button>
              <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => remove(h)} style={s.danger}>Delete</button>
            </span>
          </div>
        )
      )}
      {editingId === "new" ? form : (
        <div>
          <button className="mp-btn" disabled={busy || editingId !== null} onClick={() => startEdit(null)} style={s.secondary}>+ Add hours</button>
        </div>
      )}
      {error && <p style={s.error}>{error}</p>}
    </div>
  );
}
